import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ProfessionalLayout from '../components/ProfessionalLayout';
import EmptyState from '../components/EmptyState';
import NotificationAudit from '../components/NotificationAudit';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import api from '../lib/api';
import { auditActionLabel } from '../lib/constants';
import { ScrollText, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_COLORS = {
  CREATE: { bg: 'hsl(var(--muted))', color: 'hsl(var(--accent))', border: 'hsl(var(--accent))' },
  CREATED: { bg: 'hsl(var(--muted))', color: 'hsl(var(--accent))', border: 'hsl(var(--accent))' },
  UPDATE: { bg: 'hsl(var(--muted))', color: 'hsl(var(--foreground))', border: 'hsl(var(--border))' },
  UPDATED: { bg: 'hsl(var(--muted))', color: 'hsl(var(--foreground))', border: 'hsl(var(--border))' },
  DELETE: { bg: '#FEE2E2', color: '#DC2626', border: '#EF4444' },
  DELETED: { bg: '#FEE2E2', color: '#DC2626', border: '#EF4444' },
  LOGIN: { bg: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))', border: 'hsl(var(--border))' },
};

const getActionStyle = (action) => {
  const upper = (action || '').toUpperCase();
  for (const key of Object.keys(ACTION_COLORS)) {
    if (upper.includes(key)) return ACTION_COLORS[key];
  }
  return { bg: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))', border: 'hsl(var(--border))' };
};

const getEntityTypes = (t) => [
  { value: '', label: t('all') },
  { value: 'user', label: t('entityUser') },
  { value: 'package', label: t('entityPackage') },
  { value: 'shipment', label: t('entityShipment') },
  { value: 'company', label: t('entityCompany') },
];

const PAGE_SIZE = 25;

const AuditLogPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const ENTITY_TYPES = getEntityTypes(t);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('');
  const [page, setPage] = useState(1);

  const isAdmin = ['super_admin', 'company_admin'].includes(user?.role);

  useEffect(() => {
    if (isAdmin) fetchLogs();
  }, [entityFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entityFilter) params.append('entity_type', entityFilter);
      params.append('limit', '500');
      const response = await api.get(`${API}/api/audit-logs?${params.toString()}`);
      setLogs(response.data);
      setPage(1);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <ProfessionalLayout>
        <div style={{ textAlign: 'center', padding: 'var(--spacing-12)' }}>
          <h2 className="heading-2">{t('accessDenied')}</h2>
          <p className="body-base" style={{ color: 'hsl(var(--muted-foreground))', marginTop: 'var(--spacing-2)' }}>
            {t('adminOnlyPage')}
          </p>
        </div>
      </ProfessionalLayout>
    );
  }

  const totalPages = Math.ceil(logs.length / PAGE_SIZE);
  const paginatedLogs = logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <ProfessionalLayout>
      <div style={{ marginBottom: 'var(--spacing-6)' }}>
        <h1 className="heading-1" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
          <ScrollText size={40} style={{ color: 'var(--accent)' }} />
          {t('auditLogs')}
        </h1>
        <p className="body-base" style={{ marginTop: 'var(--spacing-2)', color: 'hsl(var(--muted-foreground))' }}>
          {t('auditLogsDesc')}
        </p>
      </div>

      <Tabs defaultValue="system">
      <TabsList className="mb-4">
        <TabsTrigger value="system">Journal système</TabsTrigger>
        <TabsTrigger value="notifications">Audit notifications</TabsTrigger>
      </TabsList>
      <TabsContent value="notifications">
        <NotificationAudit />
      </TabsContent>
      <TabsContent value="system">
      {/* Filters */}
      <div className="card-professional" style={{ marginBottom: 'var(--spacing-6)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
        <Filter size={18} style={{ color: 'hsl(var(--muted-foreground))' }} />
        <div>
          <label style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginRight: 'var(--spacing-2)' }}>
            {t('entityType')} :
          </label>
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '2px solid hsl(var(--border))',
              borderRadius: 'var(--radius)',
              fontSize: 'var(--text-sm)',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            {ENTITY_TYPES.map((et) => (
              <option key={et.value} value={et.value}>{et.label}</option>
            ))}
          </select>
        </div>
        <span style={{ fontSize: 'var(--text-sm)', color: 'hsl(var(--muted-foreground))' }}>
          {logs.length} {logs.length > 1 ? t('entries') : t('entry')}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--spacing-12)' }}>
          <div className="skeleton" style={{ width: '100%', height: '400px' }}></div>
        </div>
      ) : logs.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title={t('noLogs')}
          description={t('noLogsForFilters')}
        />
      ) : (
        <>
          <div className="card-professional" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
                <thead>
                  <tr style={{ background: 'hsl(var(--muted))', borderBottom: '2px solid hsl(var(--border))' }}>
                    <th style={thStyle}>{t('date')}</th>
                    <th style={thStyle}>{t('user')}</th>
                    <th style={thStyle}>{t('action')}</th>
                    <th style={thStyle}>{t('entity')}</th>
                    <th style={thStyle}>{t('details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLogs.map((log, idx) => {
                    const actionStyle = getActionStyle(log.action);
                    return (
                      <tr key={log.id || idx} style={{ borderBottom: '1px solid hsl(var(--muted))' }}>
                        <td style={tdStyle}>
                          {log.created_at
                            ? new Date(log.created_at).toLocaleString('fr-FR', {
                                day: '2-digit', month: '2-digit', year: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })
                            : '-'}
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontWeight: 600 }}>{log.user_email || '-'}</span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            display: 'inline-block',
                            padding: '3px 10px',
                            borderRadius: 'var(--radius)',
                            fontSize: '11px',
                            fontWeight: 700,
                            background: actionStyle.bg,
                            color: actionStyle.color,
                            border: `1px solid ${actionStyle.border}`,
                          }}>
                            {auditActionLabel(log.action, language)}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 'var(--radius)',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: 'hsl(var(--muted))',
                            color: 'hsl(var(--foreground))',
                            textTransform: 'capitalize'
                          }}>
                            {log.entity_type}
                          </span>
                          {log.entity_id && (
                            <span style={{ fontSize: '11px', color: 'hsl(var(--muted-foreground))', marginLeft: '6px' }}>
                              {log.entity_id.substring(0, 8)}...
                            </span>
                          )}
                        </td>
                        <td style={{ ...tdStyle, maxWidth: '300px' }}>
                          <span style={{ fontSize: '12px', color: 'hsl(var(--muted-foreground))', wordBreak: 'break-word' }}>
                            {log.details == null
                              ? '-'
                              : typeof log.details === 'object'
                                ? Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ')
                                : String(log.details)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--spacing-3)',
              marginTop: 'var(--spacing-6)'
            }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={paginationBtnStyle(page === 1)}
              >
                <ChevronLeft size={16} />
              </button>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'hsl(var(--muted-foreground))' }}>
                Page {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={paginationBtnStyle(page === totalPages)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
      </TabsContent>
      </Tabs>
    </ProfessionalLayout>
  );
};

const thStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontWeight: 700,
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'hsl(var(--muted-foreground))',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '12px 16px',
  verticalAlign: 'middle',
};

const paginationBtnStyle = (disabled) => ({
  padding: '8px',
  border: '1px solid hsl(var(--border))',
  borderRadius: 'var(--radius)',
  background: disabled ? 'hsl(var(--muted))' : 'white',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  display: 'flex',
  alignItems: 'center',
});

export default AuditLogPage;
